import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IOperationResultNoValue, ITemplateStatus, Industry, TemplateStatus } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { Observer, inject, observer } from 'mobx-react';
import * as React from 'react';
import { Draggable, DraggableProvided, Droppable, DroppableProvided } from 'react-beautiful-dnd';
import { CampaignType, IAdminUserSessionComponentProps } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import {
	AdminPermission,
	IndustryHtmlNewsletterViewModel,
	TemplateCategoriesViewModel,
	TemplateCategoryViewModel,
	TemplateEditViewModel,
	TemplateWithStatusesViewModel,
} from '../../../../viewmodels/AdminViewModels';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { DisclosureIcon } from '../../../../web/components/svgs/icons/DisclosureIcon';
import { DragGripperIcon } from '../../../../web/components/svgs/icons/DragGripperIcon';
import { EditItemPenIcon } from '../../../../web/components/svgs/icons/EditItemPenIcon';
import { TrashIcon } from '../../../../web/components/svgs/icons/TrashIcon';
import { grayIconFill, header } from '../../../../web/styles/colors';
import { TemplateRow } from '../TemplateRow';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IErrorMessageComponentProps, IAdminUserSessionComponentProps {
	categories?: TemplateCategoriesViewModel;
	category?: TemplateCategoryViewModel;
	campaignType?: CampaignType;
	className?: string;
	index: number;
	isSocialCombo?: boolean;
	onChangeCategory?(template?: TemplateWithStatusesViewModel, fromCategory?: TemplateCategoryViewModel): void;
	onCopyDirectLink?(template: TemplateWithStatusesViewModel): void;
	onDeleteCategory?(): void;
	onEditCategory?(): void;
	styles?: StyleDeclarationValue[];
	selectedTemplateIds?: string[];
	toggleSelectedTemplateIds?: (droppableId: string, category: TemplateCategoryViewModel) => void;
}

export type TemplateActions =
	| 'unfeature'
	| 'feature'
	| 'edit'
	| 'category'
	| 'archive'
	| 'archiveAll'
	| 'delete'
	| 'copy'
	| 'view';

class _TemplateCategory extends React.Component<IProps> {
	public render() {
		const {
			className,
			styles,
			category,
			index,
			onDeleteCategory,
			onEditCategory,
			campaignType,
			selectedTemplateIds,
			toggleSelectedTemplateIds,
			categories,
			userSession,
		} = this.props;
		const isExpanded = category?.isExpanded;

		const droppableId = `${category.industry}%${category.name}%${category.name === 'Featured' ? 'featured' : ''}`;

		return (
			<Draggable disableInteractiveElementBlocking={true} draggableId={category.name} index={index}>
				{(draggableProvided: DraggableProvided) => {
					return (
						<Observer>
							{() => {
								return (
									<div
										className={`${css(styleSheet.container, ...(styles || []))} template-category ${className || ''}`}
										ref={draggableProvided.innerRef}
										{...(draggableProvided?.draggableProps || {})}
									>
										<div className={css(styleSheet.titleRow)}>
											<div className={css(styleSheet.titleSection)}>
												<span {...(draggableProvided?.dragHandleProps || {})}>
													<DragGripperIcon />
												</span>
												<span
													className={css(
														styleSheet.title,

														category.name === 'Uncategorized' && category.total > 0 && styleSheet.uncategorized
													)}
												>
													{category.name}
												</span>
												{category.isBusy && <LoadingSpinner type='tiny' />}
											</div>
											<div className={css(styleSheet.expanderSection)}>
												{category.numberOfUnpublishedTemplates > 0 && (
													<span className={css(styleSheet.unpublished)}>
														{`${category.numberOfUnpublishedTemplates} unpublished campaign${
															category.numberOfUnpublishedTemplates > 1 ? 's' : ''
														}`}
													</span>
												)}
												<span className={css(styleSheet.total)}>{category.total}</span>
												{category.name !== 'Featured' &&
												category.name !== 'Archived' &&
												category.name !== 'Uncategorized' &&
												category.name !== IndustryHtmlNewsletterViewModel.HtmlNewslettersCategoryName ? (
													<>
														{campaignType !== CampaignType.Social &&
															userSession?.hasPermission(AdminPermission.OnlyForDevelopers) && (
																<span onClick={onEditCategory}>
																	<EditItemPenIcon className={css(styleSheet.edit)} fillColor={header} />
																</span>
															)}
														<span onClick={onDeleteCategory}>
															<TrashIcon className={css(styleSheet.trash)} fillColor={header} />
														</span>
													</>
												) : (
													<span className={css(styleSheet.trashFiller)} />
												)}
												<span
													onClick={this.onChevronClick}
													className={css(isExpanded && styleSheet.invert, styleSheet.chevron)}
												>
													<DisclosureIcon type='chevron' fillColor={category.total === 0 ? grayIconFill : ''} />
												</span>
											</div>
										</div>
										<div className={css(styleSheet.body, isExpanded && styleSheet.bodyExpanded)}>
											<div className={css(styleSheet.campaignHeader)}>
												<div className={css(styleSheet.headerName)}>Campaign Name</div>
												<div className={css(styleSheet.headerIndustries)}>Industries</div>
												<div className={css(styleSheet.headerCreated)}>Created</div>
												<div className={css(styleSheet.headerActions)}>Actions</div>
											</div>
											<Droppable
												direction='vertical'
												droppableId={droppableId}
												isDropDisabled={!isExpanded}
												type='template'
											>
												{(droppableProvided: DroppableProvided) => {
													return (
														<Observer>
															{() => {
																return (
																	<div ref={droppableProvided.innerRef} {...droppableProvided.droppableProps}>
																		{category?.isBusy ? (
																			<LoadingSpinner />
																		) : (
																			category.systemTemplates?.map((t, idx) => {
																				return (
																					<TemplateRow
																						categories={categories}
																						index={idx}
																						category={category}
																						key={t.templateReference.template.id}
																						template={t}
																						parentExpanded={isExpanded}
																						onMenuItemClicked={this.onMenuItemClicked(t)}
																						templateType={campaignType}
																						unpublished={category.isUnpublishedTemplate(
																							t.templateReference.template.id
																						)}
																						selectedTemplateIds={selectedTemplateIds}
																						toggleSelectedTemplateIds={toggleSelectedTemplateIds}
																						campaignType={campaignType}
																					/>
																				);
																			})
																		)}
																	</div>
																);
															}}
														</Observer>
													);
												}}
											</Droppable>
										</div>
									</div>
								);
							}}
						</Observer>
					);
				}}
			</Draggable>
		);
	}

	private onMenuItemClicked = (template: TemplateWithStatusesViewModel) => async (menuItem: TemplateActions) => {
		const { campaignType, category, logApiError, errorMessages, categories, userSession, onCopyDirectLink } =
			this.props;
		const isCombo =
			(campaignType === CampaignType.Social && template.templateReference.template?.associatedTemplates?.length > 0) ||
			(campaignType === CampaignType.Email && template.templateReference.template?.associatedTemplates?.length > 0);
		const templateId =
			campaignType === CampaignType.Social && template.templateReference.template?.associatedTemplates?.length > 0
				? template.templateReference.template.associatedTemplates[0].id // add a check that it is an email template type
				: template.templateReference.template.id; // this template could be either email solo, email combo, or social solo
		const comboCampaignType =
			campaignType === CampaignType.Social && template.templateReference.template?.associatedTemplates?.length > 0
				? CampaignType.Email
				: campaignType;
		const associatedTemplate = new TemplateEditViewModel(userSession);
		if (campaignType === CampaignType.Social && template.templateReference.template?.associatedTemplates?.length > 0) {
			await associatedTemplate?.load(
				template.templateReference.template?.associatedTemplates[0]?.id,
				CampaignType.Email
			);
		}
		const comboTemplate =
			campaignType === CampaignType.Social && template.templateReference.template?.associatedTemplates?.length > 0
				? new TemplateWithStatusesViewModel(userSession, associatedTemplate.getSystemTemplate(), category.industry)
				: template;
		switch (menuItem) {
			case 'unfeature':
				category
					.unfeature(templateId, comboCampaignType)
					?.then(() => {
						const featured = categories.categories?.find(x => x.name === 'Featured');

						featured.setTotal(featured.total + 1);

						featured
							.load(campaignType)

							?.then(() => featured.setExpanded(true))
							?.catch((err: IOperationResultNoValue) => {
								logApiError('LoadTemplateCategory-Error', err);

								errorMessages.pushApiError(err);
							});
					})
					?.catch((err: IOperationResultNoValue) => {
						logApiError('UnfeatureTemplate-Error', err);

						errorMessages.pushApiError(err);
					});
				break;
			case 'category':
				this.props.onChangeCategory?.(comboTemplate, category);
				break;
			case 'archive':
				try {
					const archived = categories.categories?.find(x => x.name === 'Archived');
					const featured = categories.categories?.find(x => x.name === 'Featured');

					await archived.addStatuses(
						templateId,
						[
							{
								category: 'Archived',

								industry: category.industry,
								status: TemplateStatus.Archived,
							},
						],
						comboCampaignType,
						isCombo
					);
					category.setExpanded(false);

					await category.removeStatuses(
						templateId,
						[
							{ category: 'Featured', industry: category.industry },

							{ category: category.name, industry: category.industry },
						],
						comboCampaignType,
						isCombo
					);
					if (campaignType === CampaignType.Blog) {
						await archived.load(campaignType);
						await category.load(campaignType);
						featured
							.load(campaignType)
							?.then(() => {
								featured.setExpanded(false);
							})
							?.catch((err: IOperationResultNoValue) => {
								logApiError('LoadTemplateCategory-Error', err);

								errorMessages.pushApiError(err);
							});
					}
				} catch (error) {
					logApiError('ArchiveTemplateAll-Error', error);

					errorMessages.pushApiError(error);
				}
				break;
			case 'archiveAll':
				try {
					const archived = categories.categories?.find(x => x.name === 'Archived');
					const featured = categories.categories?.find(x => x.name === 'Featured');
					const industries = template.templateReference.industries;

					const statuses: ITemplateStatus[] = Object.keys(industries).map(ind => {
						return {
							category: 'Archived',
							industry: ind as Industry,
							status: TemplateStatus.Archived,
						};
					});
					category.setExpanded(false);

					const fullTemplate = new TemplateEditViewModel(userSession, comboTemplate.templateReference.template);
					await fullTemplate.load(templateId, comboCampaignType);

					await category.removeStatuses(templateId, fullTemplate.statuses, campaignType, isCombo);

					await archived.addStatuses(templateId, statuses, campaignType, isCombo);
					if (campaignType === CampaignType.Blog) {
						await archived.load(campaignType);
						await category.load(campaignType);
						featured
							.load(campaignType)
							?.then(() => {
								featured.setExpanded(false);
							})
							?.catch((err: IOperationResultNoValue) => {
								logApiError('LoadTemplateCategory-Error', err);

								errorMessages.pushApiError(err);
							});
					}
				} catch (error) {
					logApiError('ArchiveTemplate-Error', error);

					errorMessages.pushApiError(error);
				}
				break;
			case 'copy':
				onCopyDirectLink?.(template);
				break;
			case 'delete': // this needs updating
				try {
					const fullTemplate = new TemplateEditViewModel(userSession, comboTemplate.templateReference.template);
					await fullTemplate.load(templateId, comboCampaignType);
					// removing the statuses of the template but not deleting the template itself to protect
					// any internal references etc.
					await category.removeStatuses(templateId, fullTemplate.statuses, campaignType, isCombo);
					if (campaignType === CampaignType.Blog) {
						category.load(campaignType);
						category.setExpanded(true);
					}
				} catch (error) {
					logApiError('DeleteTemplate-Error', error);
					errorMessages.pushApiError(error);
				}
				break;
			default:
				break;
		}
	};

	private onChevronClick = () => {
		const { category, logApiError, errorMessages, campaignType } = this.props;
		if (category.total === 0) {
			category.setExpanded(false);
			return;
		}

		if (!category.isExpanded) {
			category.load(campaignType)?.catch((err: IOperationResultNoValue) => {
				logApiError(`LoadTemplatesInCategory-${campaignType}-Error`, err);

				errorMessages.pushApiError(err);
			});
		}
		category.setExpanded(!category.isExpanded);
	};
}

const TemplateCategoryAsObserver = observer(_TemplateCategory);
const TemplateCategoryWithContext = inject(
	ErrorMessagesViewModelKey,
	UserSessionViewModelKey
)(TemplateCategoryAsObserver);
export const TemplateCategory = withEventLogging(TemplateCategoryWithContext, 'TemplateCategory');
